import clsx from 'clsx';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type * as hiveCore from '@gitbook/hive-core';
import { useHiveRevision } from '@gitbook/hive-react';
import { ConfirmModal, DateShortRelative } from '@gitbook/react-uikit';
import type { Change } from '@gitbook/slate';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { BasicEditor, useBasicEditorValue } from '../BasicEditor';
import { UserAvatar, UserProfilePrettyName } from '../Users';
import { CommentContextLink } from './CommentContextLink';
import { CommentMutations } from './CommentMutations';
import { CommentReviewPreview } from './CommentReviewPreview';
import styles from './Comments.module.css';
import { CommentTargetPreview } from './CommentTargetPreview';
import { CommentEmojiActions } from './Emoji/CommentEmojis';
import { useCommentActions } from './useCommentActions';

interface CommentMessageProps {
    /* The comment that the message is a reply to. */
    comment: api.Comment;

    /* The comment reply that the message is a reply to. */
    commentReply?: api.CommentReply;

    /* Whether this message is in a selected comment. */
    isSelected?: boolean;

    /* Show in which page this comment is located */
    showContextLink?: boolean;

    /* Whether the message is the last reply. */
    isLastReply?: boolean;
}

/**
 * Display a single comment or reply message.
 */
export function CommentMessage(props: CommentMessageProps): React.ReactElement {
    const { comment, commentReply, isSelected, showContextLink, isLastReply } = props;
    const message = commentReply || comment;

    const { revision } = useHiveRevision();

    const [editing, setEditing] = React.useState(false);
    const [modal, setModal] = React.useState<null | 'delete'>(null);
    const [loading, wrapLoading] = useLoadingState();
    const commentActions = useCommentActions({ comment, commentReply });
    const { value, initialValue, setValue } = useBasicEditorValue(assertJSONDocument(message.body));
    const hasEmojiReactions = message.reactions.length > 0;
    const isReply = Boolean(commentReply);

    /** Handle change on the message */
    const onChange = (change: Change) => {
        if (!editing) {
            return;
        }

        setValue(change.value);
    };

    /** User starts making edits */
    const onStartEdits = () => {
        setValue(initialValue.change().focus().value);
        setEditing(true);
    };

    /** User is saving changes */
    const onSaveEdits = () => {
        wrapLoading(async () => {
            await commentActions.onEdit(value.document);
            setEditing(false);
        });
    };

    /** User is cancelling its changes */
    const onCancelEdits = () => {
        setValue(initialValue);
        setEditing(false);
    };

    /** User confirmed deletion */
    const onCompleteDeletion = async (deleted: boolean) => {
        if (deleted) {
            await commentActions.onDelete();
        }
        setModal(null);
    };

    const onDeleteAttempt = () => {
        setModal('delete');
    };

    // The line shows if a comment is either 1. the parent comment with a reply, or 2. a reply, but not the last.
    const showThreadLine = (!isReply && comment.replies > 0) || (isReply && !isLastReply);

    return (
        <div
            className={clsx(
                'group/message relative flex w-full origin-top-left gap-4',
                showThreadLine && styles.threadLine
            )}
        >
            <UserAvatar user={message.postedBy} size="sm" />

            <div className="flex w-full flex-col items-start">
                <div className="relative flex w-full items-center gap-1 font-bold text-base text-ui-action">
                    <UserProfilePrettyName
                        user={{
                            id: message.postedBy.id,
                            displayName: message.postedBy.displayName,
                        }}
                        capitalize
                    />
                    <DateShortRelative
                        value={new Date(message.postedAt)}
                        onlyDate={false}
                        className="w-fit text-muted text-ui-small"
                    />

                    {comment.target.page && revision && showContextLink ? (
                        <CommentContextLink
                            page={
                                revision.pages[
                                    comment.target.page.id
                                ] as hiveCore.RevisionPageDocument
                            }
                            prefix="in"
                        />
                    ) : null}

                    <CommentMutations
                        comment={comment}
                        commentReply={commentReply}
                        isReply={isReply}
                        onStartEdits={onStartEdits}
                        onDeleteAttempt={onDeleteAttempt}
                        onCancelEdits={onCancelEdits}
                        onSaveEdits={onSaveEdits}
                        editing={editing}
                        loadingEdit={loading}
                    />
                </div>

                {'target' in message && message.target.page?.id ? (
                    <div className="pb-2">
                        <CommentTargetPreview
                            spaceId={message.target.space}
                            pageId={message.target.page?.id}
                            tableCell={message.target.tableCell}
                            nodeKey={message.target.node?.id}
                        />
                    </div>
                ) : null}

                {'target' in message && message.target.changeRequest && message.target.review ? (
                    <React.Suspense fallback={null}>
                        <CommentReviewPreview
                            spaceId={message.target.space}
                            changeRequestId={message.target.changeRequest}
                            reviewId={message.target.review}
                        />
                    </React.Suspense>
                ) : null}

                {/* The message content (as an Editor to allow the author to edit-in-place) */}
                <BasicEditor
                    value={value}
                    onChange={onChange}
                    onSubmit={onSaveEdits}
                    readOnly={!editing || loading}
                    autoFocus
                    className={clsx(
                        'dont-break-out text-ui-base',
                        comment.status === 'resolved' ? 'text-light' : 'text-secondary'
                    )}
                />

                {/* Emoji reactions, if applicable */}
                {hasEmojiReactions ? (
                    <CommentEmojiActions
                        comment={message}
                        commentActions={commentActions}
                        opened={isSelected}
                    />
                ) : null}
            </div>

            {/* The delete confirmation modal */}
            {modal === 'delete' ? (
                <ConfirmModal
                    kind="danger"
                    title="Delete comment"
                    confirmButtonText="Delete"
                    onComplete={onCompleteDeletion}
                >
                    You are deleting a comment, and this cannot be undone. Would you like to
                    continue?
                </ConfirmModal>
            ) : null}
        </div>
    );
}

function assertJSONDocument(input: api.Document): api.JSONDocument {
    if ('document' in input) {
        return input.document;
    }

    throw new Error('Expected a JSONDocument');
}
